<script setup lang="ts">
import { defineProps } from 'vue';
import ContextMenuButton from '../ContextMenu/ContextMenuButton.vue';
import UserRoleSelector from '../UserRoleSelector.vue';
import Dropdown from '../Dropdown/Dropdown.vue';
import DropdownTitleHover from '../Dropdown/DropdownTitleHover.vue';
import TeamUser from '../../../../types/teamUser';
import type { CurrentUser } from '../../../../types/current_user';
import DropDownArrowIcon from '@/dashboard/vue/components/icons/DropdownArrowIcon.vue';
import Divider from '@/dashboard/vue/components/common/Divider.vue';
import Button from '@/dashboard/vue/components/common/Button.vue';
import { useAddTeamList } from '@/dashboard/vue/components/common/AddTeamList/useAddTeamList';
import getRoleEmoji from '@/dashboard/utils/user/getRoleEmoji';
import AlertInfoIcon from '@/dashboard/vue/components/icons/AlertInfoIcon.vue';
import { UserRole } from '@/dashboard/types/user_role';
import InfoIcon from '@/dashboard/vue/components/icons/InfoIcon.vue';
import Tooltip from '@/dashboard/vue/components/common/Tooltip.vue';
import UserRoleTooltip from '@/dashboard/vue/components/tooltips/userRoleTooltip.vue';

type AddTeamListProps = {
  isCreate: boolean;
  currentUser: CurrentUser;
  team: TeamUser[];
  onTeamUpdated: (team: TeamUser[]) => void;
  type: 'workspace' | 'project';
};

const props = defineProps<AddTeamListProps>();
const { email, nameHasError, roles, role, canAddCreators, canAddUser, currentTeam, add, changeRole, remove } = useAddTeamList({ ...props });
</script>

<template>
  <div class="flex flex-1 flex-col pb-[42px]">
    <div class="flex items-start">
      <div class="tppr-text-field flex-1" :class="{ error: nameHasError }">
        <div class="tppr-text-field__title flex justify-between text-left text-gray-700 dark:text-gray-500">
          <span>Add a teammate</span>
        </div>

        <div class="tppr-text-field__field-container">
          <input v-model="email" type="text" placeholder="email" class="!pr-[190px]" />
          <div class="absolute right-s !w-auto">
            <div class="flex items-center justify-end gap-xs">
              <Dropdown :body-class="'z-[6001] gap-[2px] p-[6px]'">
                <template #title="{ isOpen }: any">
                  <DropdownTitleHover :is-active="isOpen">
                    <div class="flex items-center gap-xs">
                      <span class="text-caption-s capitalize text-black dark:text-gray-500">{{ role }}</span>
                      <DropDownArrowIcon class="stroke-black dark:stroke-gray-500" />
                    </div>
                  </DropdownTitleHover>
                </template>
                <template #body>
                  <div v-for="role_item in roles" :key="role_item" class="flex flex-col">
                    <ContextMenuButton :need-dark="true" :action="() => (role = role_item)">
                      <div class="flex items-center gap-[10px] whitespace-nowrap">
                        <span class="text-caption-s">{{ getRoleEmoji(role_item) }}</span>
                        <span class="text-caption-s capitalize text-black dark:text-white">{{ role_item }}</span>

                        <Tooltip>
                          <template #title>
                            <AlertInfoIcon v-if="role_item === UserRole.Admin" />
                            <InfoIcon v-else class="cursor-pointer stroke-gray-400 dark:stroke-gray-650" />
                          </template>
                          <template #body>
                            <UserRoleTooltip :user-role="role_item" :need-admin-error="true" />
                          </template>
                        </Tooltip>
                      </div>
                    </ContextMenuButton>
                  </div>
                </template>
              </Dropdown>
              <Button
                :on-click="add"
                class="button-colored h-[32px]"
                :class="{ '!bg-gray-300 dark:!bg-gray-850 dark:!text-gray-600': !canAddUser }"
                :disabled="!canAddUser"
              >
                <span class="button__text">Invite</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="relative mt-m flex flex-1">
      <div class="absolute left-zero right-zero top-zero mr-[-12px] h-full overflow-y-scroll pr-[6px]">
        <div v-for="(user, index) in currentTeam" :key="user.hash">
          <UserRoleSelector
            :user="user"
            :current-user="props.currentUser"
            :can-add-creator="canAddCreators"
            :change-role="changeRole"
            :remove="remove"
          />

          <Divider v-if="(index as number) < currentTeam.length - 1" class="divider-gray-450 dark:divider-gray-750 my-xs w-full" />
        </div>
      </div>
    </div>
  </div>
</template>
